/**
 * Share-listing helpers.
 *
 * V1 ships a lightweight share UX (native share sheet on mobile,
 * WhatsApp deep link, copy-to-clipboard) without integrating into the
 * platform's share-link analytics service — that integration is a
 * separate piece of work because the share-link system is project-
 * scoped and listings aren't.
 *
 * Branded PNG share cards (with QR code + realtor branding) are also a
 * follow-up — V1 lets WhatsApp's own link unfurl render the rich
 * preview from the public listing's first photo + metadata.
 */

import type { Listing } from "@/features/realtor/services/listings-api.service";
import { formatPaiseAsRupees } from "./listings-format";

export interface ShareableListing {
	headline: string;
	locality: string;
	city: string;
	askingPrice: number;
	publicSlug: string | null;
}

export interface ListingShareContent {
	url: string;
	title: string;
	message: string;
	whatsappUrl: string;
}

/**
 * Build the canonical share content for a listing. The URL points at the
 * public listing page using the slug and the current origin, so a copy
 * pasted link works whether the realtor is on staging or prod.
 *
 * Returns null when the listing has no public slug (i.e. unpublished or
 * deleted) — callers should hide the share button in that state.
 */
export function buildListingShareContent(
	listing: ShareableListing,
	origin: string = window.location.origin,
): ListingShareContent | null {
	if (!listing.publicSlug) return null;
	const url = `${origin.replace(/\/$/, "")}/listings/${listing.publicSlug}`;
	const title = listing.headline;
	const price = formatPaiseAsRupees(listing.askingPrice);
	const message = [
		`🏠 ${listing.headline}`,
		`${price} · ${listing.locality}, ${listing.city}`,
		url,
	].join("\n");
	const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
	return { url, title, message, whatsappUrl };
}

/** Narrow `Listing` to the share-relevant subset. */
export function toShareableListing(listing: Listing): ShareableListing {
	return {
		headline: listing.headline,
		locality: listing.locality,
		city: listing.city,
		askingPrice: listing.askingPrice,
		publicSlug: listing.publicSlug,
	};
}

/**
 * Best-effort native share sheet. Returns true if the share dialog was
 * shown (and resolved or cancelled), false if the API isn't available
 * or the call threw something other than user-cancel. Callers fall
 * back to manual copy/WhatsApp on false.
 *
 * AbortError = user dismissed the sheet (treated as success: we don't
 * pester them with a fallback toast). NotAllowedError = Safari's "not
 * called from a user gesture" rejection — same UX outcome.
 */
export async function shareViaBrowser(
	content: ListingShareContent,
): Promise<boolean> {
	if (typeof navigator === "undefined" || !navigator.share) return false;
	try {
		await navigator.share({
			title: content.title,
			text: content.message,
			url: content.url,
		});
		return true;
	} catch (error) {
		if (error instanceof Error) {
			if (error.name === "AbortError" || error.name === "NotAllowedError") {
				return true;
			}
		}
		return false;
	}
}

/**
 * Copy a string to the clipboard using the modern API, falling back to
 * the deprecated execCommand path for older browsers / non-secure
 * contexts that the modern API rejects.
 */
export async function copyToClipboard(value: string): Promise<boolean> {
	if (navigator.clipboard?.writeText) {
		try {
			await navigator.clipboard.writeText(value);
			return true;
		} catch {
			// Fall through to legacy path on permission errors.
		}
	}
	try {
		const textArea = document.createElement("textarea");
		textArea.value = value;
		// Position fully off-screen + readonly so iOS Safari doesn't
		// briefly steal focus or scroll the page when we select() it.
		textArea.setAttribute("readonly", "");
		textArea.style.position = "fixed";
		textArea.style.left = "-9999px";
		textArea.style.top = "0";
		textArea.style.opacity = "0";
		document.body.appendChild(textArea);
		textArea.select();
		const ok = document.execCommand("copy");
		textArea.remove();
		return ok;
	} catch {
		return false;
	}
}
